import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const EmployeeSatisfactionIndex = () => {
  // State variables for Employee Satisfaction Index calculation
  const [positiveResponses, setPositiveResponses] = useState(0); // Number of positive responses
  const [totalResponses, setTotalResponses] = useState(0); // Total number of responses
  const [satisfactionIndex, setSatisfactionIndex] = useState(0); // Calculated employee satisfaction index

  // Event handlers to update state values
  const handlePositiveResponsesChange = (e) => setPositiveResponses(parseFloat(e.target.value) || 0);
  const handleTotalResponsesChange = (e) => setTotalResponses(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate employee satisfaction index when any input changes
  useEffect(() => {
    if (totalResponses > 0) {
      const index = (positiveResponses / totalResponses) * 100;
      setSatisfactionIndex(index);
    } else {
      setSatisfactionIndex(0);
    }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [positiveResponses, totalResponses]);

  return (
    <Layout title="Employee Satisfaction Index Calculator" path="employee-satisfaction-index">
      <section className="section-padding" id="employee-satisfaction-index-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Employee Satisfaction Index Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Employee Satisfaction Index Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Positive Responses Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="positiveResponses" className="form-label">
                        Positive Responses
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Positive Responses:</strong> The number of responses that are considered positive, typically reflecting employee satisfaction with their work environment."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="positiveResponses"
                        placeholder="Enter number of positive responses"
                        value={positiveResponses || ""}
                        onChange={handlePositiveResponsesChange}
                      />
                    </div>

                    {/* Total Responses Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalResponses" className="form-label">
                        Total Responses
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Responses:</strong> The total number of responses gathered from employees regarding their satisfaction, including both positive and negative responses."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalResponses"
                        placeholder="Enter total number of responses"
                        value={totalResponses || ""}
                        onChange={handleTotalResponsesChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Employee Satisfaction Index:{" "}
                        <span className="fw-bold ms-3">
                          {satisfactionIndex === 0 ? "0.00" : satisfactionIndex.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Employee Satisfaction Index Calculator
                </h3>

                {/* What is an Employee Satisfaction Index Calculator? */}
                <h4 className="mt-3 mb-3">What is an Employee Satisfaction Index Calculator?</h4>
                <p>
                  The **Employee Satisfaction Index Calculator** helps you calculate the level of employee satisfaction based on the percentage of positive responses received from employee surveys.
                </p>

                {/* Why Use an Employee Satisfaction Index Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Employee Satisfaction Index Calculator?</h4>
                <p>
                  This tool provides insights into employee morale, highlighting areas where HR can focus on improvement and retention strategies.
                </p>

                {/* Benefits of Using an Employee Satisfaction Index Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Employee Satisfaction Index Calculator</h4>
                <ul>
                  <li><strong>Measures Employee Morale:</strong> Helps track the overall satisfaction level of employees.</li>
                  <li><strong>Identifies Areas for Improvement:</strong> Reveals potential issues that may affect retention and productivity.</li>
                  <li><strong>Supports Retention Strategies:</strong> Provides actionable data to improve employee satisfaction and retention.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Employee Satisfaction Index Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>HR Departments:</strong> To monitor employee satisfaction and identify areas for improvement.</li>
                  <li><strong>Corporate Management:</strong> To understand the impact of employee satisfaction on overall business performance.</li>
                  <li><strong>Organizations Focused on Retention:</strong> Helps track satisfaction levels to develop effective retention strategies.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How can employee satisfaction impact business success?</h4>
                <p>
                  High employee satisfaction is directly linked to increased productivity, lower turnover rates, and a positive company culture, all of which contribute to business success.
                </p>

                <h4 className="mt-4 mb-3">How can HR use satisfaction data to improve retention?</h4>
                <p>
                  HR can use the data to identify common concerns among employees and address them through targeted initiatives, such as improving work conditions, offering better benefits, or enhancing communication.
                </p>

                <h4 className="mt-4 mb-3">What is a good employee satisfaction score?</h4>
                <p>
                  While a score of 80% or above is generally considered good, it varies by industry. The higher the score, the better the employee morale.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EmployeeSatisfactionIndex;
